// @ts-nocheck
import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Pencil,
  Copy,
  Check,
  Loader2,
  Lock,
  Unlock,
  CheckCircle2,
  XCircle,
  Send,
  Trash2,
  ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";
import { getSessionUser } from '@/lib/authSession';

const fmtCurrency = (n: number | null | undefined) =>
  n == null
    ? "—"
    : new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(n));

const fmtDate = (s: string | null | undefined) =>
  s ? format(new Date(s), "MMM d, yyyy h:mm a") : "—";

function CopyBtn({ value }: { value?: string | null }) {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  return (
    <button
      onClick={() => {
        navigator.clipboard.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
      }}
      className="text-muted-foreground hover:text-foreground"
      title="Copy"
      type="button"
    >
      {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
    </button>
  );
}

function EditableField({
  value,
  onSave,
  type = "text",
  placeholder,
  options,
  display,
}: {
  value: any;
  onSave: (v: any) => Promise<void>;
  type?: "text" | "email" | "tel" | "number" | "datetime-local" | "textarea" | "select";
  placeholder?: string;
  options?: Array<{ value: string; label: string }>;
  display?: (v: any) => React.ReactNode;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<any>(value ?? "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraft(value ?? "");
  }, [value]);

  const handleSave = async () => {
    if ((draft ?? "") === (value ?? "")) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(draft === "" ? null : draft);
      setEditing(false);
    } catch (err: any) {
      toast.error(err?.message || "Save failed");
      setDraft(value ?? "");
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        className="group flex items-center gap-2 text-sm text-foreground hover:text-primary w-full text-left min-h-[2rem]"
      >
        <span className="flex-1">
          {value || value === 0
            ? display
              ? display(value)
              : String(value)
            : <em className="text-muted-foreground font-normal">Not set</em>}
        </span>
        <Pencil className="w-3 h-3 opacity-0 group-hover:opacity-100 shrink-0" />
      </button>
    );
  }

  const inputCls =
    "flex-1 bg-background border border-input rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring";

  return (
    <div className="flex items-start gap-2">
      {type === "select" ? (
        <select
          value={draft ?? ""}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSave();
            if (e.key === "Escape") setEditing(false);
          }}
          autoFocus
          className={inputCls}
        >
          <option value="">—</option>
          {(options || []).map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      ) : type === "textarea" ? (
        <textarea
          value={draft ?? ""}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Escape") setEditing(false);
          }}
          rows={3}
          autoFocus
          className={inputCls}
          placeholder={placeholder}
        />
      ) : (
        <input
          type={type}
          value={draft ?? ""}
          onChange={(e) =>
            setDraft(type === "number" ? e.target.value : e.target.value)
          }
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSave();
            if (e.key === "Escape") setEditing(false);
          }}
          placeholder={placeholder}
          autoFocus
          className={inputCls}
        />
      )}
      <Button size="sm" onClick={handleSave} disabled={saving}>
        {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : "Save"}
      </Button>
      <Button
        size="sm"
        variant="ghost"
        onClick={() => {
          setDraft(value ?? "");
          setEditing(false);
        }}
      >
        Cancel
      </Button>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="border-t border-border pt-4">
      <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
        {title}
      </h3>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[140px_1fr] gap-3 items-start">
      <span className="text-xs text-muted-foreground pt-1.5">{label}</span>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

interface Props {
  registrationId: string | null;
  eventId: string;
  onClose: () => void;
  levelsEnabled: boolean;
  levels: Array<{ id: string; label: string }>;
  invalidateList: () => void;
}

export default function RegistrationDetailDrawer({
  registrationId,
  eventId,
  onClose,
  levelsEnabled,
  levels,
  invalidateList,
}: Props) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const open = !!registrationId;

  const [confirmPayOpen, setConfirmPayOpen] = useState(false);
  const [payAmount, setPayAmount] = useState("");
  const [payMethod, setPayMethod] = useState("");
  const [payReference, setPayReference] = useState("");

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [emailWarnPending, setEmailWarnPending] = useState<string | null>(null);

  const { data: detail, isLoading, error: detailError } = useQuery({
    queryKey: ["registration-detail", registrationId],
    enabled: !!registrationId,
    queryFn: async () => {
      const { data: reg, error } = await (supabase as any)
        .from("event_registrations_v2")
        .select("*")
        .eq("id", registrationId)
        .maybeSingle();
      if (error) throw error;
      if (!reg) throw new Error("Registration not found");

      const ids = [
        reg.coach_user_id,
        reg.registered_by_user_id,
        reg.checked_in_by,
        reg.roster_locked_by,
      ].filter(Boolean);

      const teamP = reg.team_id
        ? (supabase as any)
            .from("teams")
            .select(
              "id, display_name, canonical_name, primary_coach_user_id, primary_coach_name, logo_url, home_city, home_state"
            )
            .eq("id", reg.team_id)
            .maybeSingle()
            .then((r: any) => r.data)
            .catch(() => null)
        : Promise.resolve(null);

      const profilesP = ids.length
        ? (supabase as any)
            .from("profiles")
            .select("id, user_id, name, email, phone")
            .in("user_id", ids)
            .then((r: any) => r.data || [])
            .catch(() => [])
        : Promise.resolve([]);

      const phoneP = reg.coach_user_id
        ? (supabase as any)
            .from("user_phone_numbers")
            .select("phone_e164, is_primary")
            .eq("user_id", reg.coach_user_id)
            .eq("is_primary", true)
            .maybeSingle()
            .then((r: any) => r.data)
            .catch(() => null)
        : Promise.resolve(null);

      const [team, profiles, phone] = await Promise.all([teamP, profilesP, phoneP]);

      const profilesByUser: Record<string, any> = {};
      (profiles || []).forEach((p: any) => {
        profilesByUser[p.user_id] = p;
      });

      return {
        ...reg,
        team,
        coach: reg.coach_user_id ? profilesByUser[reg.coach_user_id] : null,
        registered_by: reg.registered_by_user_id ? profilesByUser[reg.registered_by_user_id] : null,
        checked_in_by_profile: reg.checked_in_by ? profilesByUser[reg.checked_in_by] : null,
        roster_locked_by_profile: reg.roster_locked_by ? profilesByUser[reg.roster_locked_by] : null,
        coach_phone: reg.coach_phone || phone?.phone_e164 || null,
      };
    },
  });

  const { data: divisions = [] } = useQuery({
    queryKey: ["event-divisions", eventId],
    enabled: !!eventId && open,
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from("event_divisions")
        .select("id, name")
        .eq("event_id", eventId)
        .order("sort_order", { ascending: true });
      return data || [];
    },
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["registration-detail", registrationId] });
    invalidateList();
  };

  const updateReg = async (patch: Record<string, any>) => {
    const { error } = await (supabase as any)
      .from("event_registrations_v2")
      .update(patch)
      .eq("id", registrationId);
    if (error) throw error;
    refresh();
  };

  const handleEmailChange = async (newEmail: string) => {
    await updateReg({ primary_billing_email: newEmail });

    if (detail?.coach_user_id) {
      const { error: pErr } = await (supabase as any)
        .from("profiles")
        .update({ email: newEmail })
        .eq("user_id", detail.coach_user_id);

      if (pErr) {
        console.warn("Profile email was not updated; registration email was saved.", pErr);
      }
    }

    toast.success("Email updated");
  };

  const handlePhoneChange = async (newPhone: string) => {
    // Always save phone on the registration so admins can record it even
    // when no coach account is linked yet.
    await updateReg({ coach_phone: newPhone });

    // If a coach account is linked, also sync to their profile + primary phone.
    if (detail?.coach_user_id) {
      try {
        await (supabase as any)
          .from("profiles")
          .update({ phone: newPhone })
          .eq("user_id", detail.coach_user_id);

        const { data: existing } = await (supabase as any)
          .from("user_phone_numbers")
          .select("id")
          .eq("user_id", detail.coach_user_id)
          .eq("is_primary", true)
          .maybeSingle();

        if (existing?.id) {
          await (supabase as any)
            .from("user_phone_numbers")
            .update({ phone_e164: newPhone })
            .eq("id", existing.id);
        } else {
          await (supabase as any)
            .from("user_phone_numbers")
            .insert({ user_id: detail.coach_user_id, phone_e164: newPhone, is_primary: true });
        }
      } catch (e) {
        console.warn("Phone saved on registration but coach profile sync failed:", e);
      }
    }

    toast.success("Phone updated");
    refresh();
  };

  const handleNameChange = async (newName: string) => {
    if (detail?.coach_user_id) {
      const { error } = await (supabase as any)
        .from("profiles")
        .update({ name: newName })
        .eq("user_id", detail.coach_user_id);
      if (error) throw error;
      toast.success("Coach name updated");
      refresh();
    } else {
      await updateReg({ display_name_override: newName });
      toast.success("Display name updated");
    }
  };

  const handleMarkPayment = async () => {
    const amt = parseFloat(payAmount);
    if (!isFinite(amt) || amt <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    const { data: u } = await getSessionUser();
    const due = Number(detail?.total_amount_due || 0);
    const newPaid = Number(detail?.total_amount_paid || 0) + amt;
    const newStatus = newPaid >= due ? "paid" : "partial";
    try {
      await updateReg({
        payment_received_at: new Date().toISOString(),
        payment_received_amount: amt,
        payment_received_method: payMethod || null,
        payment_received_reference: payReference || null,
        payment_marked_received_by: u?.user?.id || null,
        total_amount_paid: newPaid,
        payment_status: newStatus,
      });
      toast.success("Payment recorded");
      setConfirmPayOpen(false);
      setPayAmount("");
      setPayMethod("");
      setPayReference("");
    } catch (e: any) {
      toast.error(e?.message || "Failed");
    }
  };

  const handleToggleLock = async () => {
    const { data: u } = await getSessionUser();
    if (detail?.roster_locked) {
      await updateReg({ roster_locked: false, roster_locked_at: null, roster_locked_by: null });
      toast.success("Roster unlocked");
    } else {
      await updateReg({
        roster_locked: true,
        roster_locked_at: new Date().toISOString(),
        roster_locked_by: u?.user?.id || null,
      });
      toast.success("Roster locked");
    }
  };

  const handleToggleCheckIn = async () => {
    const { data: u } = await getSessionUser();
    if (detail?.checked_in_at) {
      await updateReg({ checked_in_at: null, checked_in_by: null });
      toast.success("Check-in undone");
    } else {
      await updateReg({
        checked_in_at: new Date().toISOString(),
        checked_in_by: u?.user?.id || null,
      });
      toast.success("Checked in");
    }
  };

  const handleDelete = async () => {
    const { data: gone, error } = await (supabase as any)
      .from("event_registrations_v2")
      .delete()
      .eq("id", registrationId)
      .select("id");
    if (error) {
      toast.error(error.message);
      return;
    }
    if (!gone?.length) {
      toast.error("This registration couldn't be deleted. You may not have permission to delete it.");
      return;
    }
    toast.success("Registration deleted");
    invalidateList();
    onClose();
  };

  const teamName =
    detail?.display_name_override ||
    detail?.team?.display_name ||
    detail?.team?.canonical_name ||
    "Team";

  if (!open) {
    return null;
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-50 bg-black/40"
        onClick={onClose}
        aria-hidden
      />
      {/* Slide-over panel */}
      <div className="fixed inset-y-0 right-0 z-50 w-full max-w-xl bg-background shadow-xl border-l border-border flex flex-col overflow-y-auto">
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b bg-muted/30 sticky top-0 z-10">
          <div className="flex items-center gap-3">
            {detail?.team?.logo_url && (
              <img
                src={detail.team.logo_url}
                alt=""
                className="w-8 h-8 rounded object-cover"
              />
            )}
            <h2 className="font-semibold">{teamName}</h2>
          </div>
          <Button size="sm" variant="ghost" onClick={onClose}>Close</Button>
        </div>

        <div className="px-4">
          {detailError ? (
            <div className="py-6 text-sm text-destructive">
              Failed to load: {(detailError as any)?.message || "Unknown error"}
            </div>
          ) : isLoading || !detail ? (
            <div className="py-8 text-center text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin mx-auto" />
            </div>
          ) : (
            <div className="space-y-5 py-4 pb-32">
              {/* Team header info */}
              <div className="text-sm text-muted-foreground space-y-1">
                {(detail.team?.home_city || detail.team?.home_state) && (
                  <div>
                    {[detail.team?.home_city, detail.team?.home_state]
                      .filter(Boolean)
                      .join(", ")}
                  </div>
                )}
                <div>Registered {fmtDate(detail.registered_at || detail.created_at)}</div>
              </div>

              {/* Coach / Contact */}
              <Section title="Coach / Contact">
                <Field label="Coach name">
                  <EditableField
                    value={detail.coach?.name || detail.team?.primary_coach_name || ""}
                    onSave={handleNameChange}
                  />
                </Field>
                <Field label="Coach email">
                  <EditableField
                    value={detail.primary_billing_email || detail.coach?.email || ""}
                    type="email"
                    onSave={handleEmailChange}
                  />
                </Field>
                <Field label="Coach phone">
                  <EditableField
                    value={detail.coach_phone || detail.coach?.phone || ""}
                    type="tel"
                    onSave={handlePhoneChange}
                  />
                </Field>
                <Field label="Billing email">
                  <EditableField
                    value={detail.primary_billing_email || ""}
                    type="email"
                    onSave={async (v) => updateReg({ primary_billing_email: v })}
                  />
                </Field>
              </Section>

              {/* Status */}
              <Section title="Registration Status">
                <Field label="Reg status">
                  <Badge variant="outline">{detail.registration_status || "—"}</Badge>
                </Field>
                <Field label="Payment">
                  <Badge
                    variant={
                      detail.payment_status === "paid"
                        ? "default"
                        : (detail.payment_status === "unpaid" ||
                            detail.payment_status === "partial" ||
                            detail.payment_status === "pending")
                        ? "secondary"
                        : "outline"
                    }
                  >
                    {detail.payment_status || "—"}
                  </Badge>
                </Field>
                <Field label="Total due">
                  <span className="text-sm">{fmtCurrency(detail.total_amount_due)}</span>
                </Field>
                <Field label="Paid">
                  <span className="text-sm">{fmtCurrency(detail.total_amount_paid)}</span>
                </Field>
                <Field label="Refunded">
                  <span className="text-sm">{fmtCurrency(detail.total_amount_refunded)}</span>
                </Field>
                <Field label="Registered by">
                  <span className="text-sm">
                    {detail.registered_by?.name || "—"}
                  </span>
                </Field>
                <Field label="Coach claimed">
                  <span className="text-sm">{detail.coach_claimed_at ? fmtDate(detail.coach_claimed_at) : "Not claimed"}</span>
                </Field>
                <Field label="Roster locked">
                  <span className="text-sm">
                    {detail.roster_locked
                      ? `${fmtDate(detail.roster_locked_at)} by ${detail.roster_locked_by_profile?.name || "—"}`
                      : "No"}
                  </span>
                </Field>
                <Field label="Checked in">
                  <span className="text-sm">
                    {detail.checked_in_at
                      ? `${fmtDate(detail.checked_in_at)} by ${detail.checked_in_by_profile?.name || "—"}`
                      : "No"}
                  </span>
                </Field>
              </Section>

              {/* Division / Pool */}
              <Section title="Division / Pool">
                <Field label="Division">
                  <EditableField
                    value={detail.division || ""}
                    type="select"
                    options={divisions.map((d: any) => ({ value: d.name, label: d.name }))}
                    onSave={async (v) => updateReg({ division: v })}
                  />
                </Field>
                <Field label="Age group">
                  <EditableField
                    value={detail.age_group || ""}
                    onSave={async (v) => updateReg({ age_group: v })}
                  />
                </Field>
                {levelsEnabled && (
                  <Field label="Pool">
                    <EditableField
                      value={detail.pool || ""}
                      type="select"
                      options={levels.map((l: any) => ({ value: l.label, label: l.label }))}
                      onSave={async (v) => updateReg({ pool: v })}
                    />
                  </Field>
                )}
                <Field label="Display name">
                  <EditableField
                    value={detail.display_name_override || ""}
                    placeholder={detail.team?.display_name}
                    onSave={async (v) => updateReg({ display_name_override: v })}
                  />
                </Field>
              </Section>

              {/* Payment Details */}
              <Section title="Payment Details">
                <Field label="Method">
                  <span className="text-sm">{detail.payment_method_chosen || "—"}</span>
                </Field>
                <Field label="Plan enabled">
                  <span className="text-sm">
                    {detail.payment_plan_enabled
                      ? `Yes (${detail.payment_plan_subscription_id || "—"})`
                      : "No"}
                  </span>
                </Field>
                <Field label="Stripe session">
                  <div className="flex items-center gap-2 text-xs font-mono break-all">
                    <span className="truncate">{detail.stripe_session_id || "—"}</span>
                    <CopyBtn value={detail.stripe_session_id} />
                  </div>
                </Field>
                <Field label="Payment intent">
                  <div className="flex items-center gap-2 text-xs font-mono break-all">
                    <span className="truncate">{detail.stripe_payment_intent_id || "—"}</span>
                    <CopyBtn value={detail.stripe_payment_intent_id} />
                  </div>
                </Field>
                <Field label="Late fee">
                  <span className="text-sm">
                    {fmtCurrency(detail.late_fee_charged)} charged · {fmtCurrency(detail.late_fee_received)} received
                  </span>
                </Field>
                <Field label="Due at">
                  <EditableField
                    value={detail.payment_due_at ? detail.payment_due_at.slice(0, 16) : ""}
                    type="datetime-local"
                    onSave={async (v) => updateReg({ payment_due_at: v ? new Date(v).toISOString() : null })}
                    display={(v) => fmtDate(v)}
                  />
                </Field>
                <Field label="Promise notes">
                  <EditableField
                    value={detail.payment_promise_notes || ""}
                    type="textarea"
                    onSave={async (v) => updateReg({ payment_promise_notes: v })}
                  />
                </Field>
                <Field label="Received at">
                  <EditableField
                    value={detail.payment_received_at ? detail.payment_received_at.slice(0, 16) : ""}
                    type="datetime-local"
                    onSave={async (v) => updateReg({ payment_received_at: v ? new Date(v).toISOString() : null })}
                    display={(v) => fmtDate(v)}
                  />
                </Field>
                <Field label="Received method">
                  <EditableField
                    value={detail.payment_received_method || ""}
                    onSave={async (v) => updateReg({ payment_received_method: v })}
                  />
                </Field>
                <Field label="Reference">
                  <EditableField
                    value={detail.payment_received_reference || ""}
                    onSave={async (v) => updateReg({ payment_received_reference: v })}
                  />
                </Field>
                <Field label="Received amt">
                  <EditableField
                    value={detail.payment_received_amount ?? ""}
                    type="number"
                    onSave={async (v) => updateReg({ payment_received_amount: v ? Number(v) : null })}
                    display={(v) => fmtCurrency(Number(v))}
                  />
                </Field>
                <div className="pt-2">
                  <Button onClick={() => setConfirmPayOpen(true)} variant="default" size="sm">
                    Mark payment received
                  </Button>
                </div>
              </Section>

              {/* Notes */}
              <Section title="Notes">
                <Field label="Registrant">
                  <EditableField
                    value={detail.registrant_notes || ""}
                    type="textarea"
                    onSave={async (v) => updateReg({ registrant_notes: v })}
                  />
                </Field>
                <Field label="Admin">
                  <EditableField
                    value={detail.admin_notes || ""}
                    type="textarea"
                    onSave={async (v) => updateReg({ admin_notes: v })}
                  />
                </Field>
              </Section>

              {/* Special Requests / Extras */}
              <Section title="Special Requests / Extras">
                <Field label="Special req.">
                  <span className="text-sm">{detail.special_requests || "—"}</span>
                </Field>
                <Field label="Special fee">
                  <span className="text-sm">{fmtCurrency(detail.special_requests_fee)}</span>
                </Field>
                {detail.extras_summary && Array.isArray(detail.extras_summary) && detail.extras_summary.length > 0 && (
                  <Field label="Extras">
                    <ul className="text-sm space-y-1">
                      {detail.extras_summary.map((x: any, i: number) => (
                        <li key={i}>
                          {x.name || x.label || JSON.stringify(x)} {x.qty ? `× ${x.qty}` : ""} {x.price ? `— ${fmtCurrency(x.price)}` : ""}
                        </li>
                      ))}
                    </ul>
                  </Field>
                )}
                {detail.custom_field_responses && Object.keys(detail.custom_field_responses || {}).length > 0 && (
                  <Field label="Custom fields">
                    <dl className="text-sm space-y-1">
                      {Object.entries(detail.custom_field_responses).map(([k, v]: any) => (
                        <div key={k}>
                          <span className="text-muted-foreground">{k}: </span>
                          <span>{typeof v === "object" ? JSON.stringify(v) : String(v)}</span>
                        </div>
                      ))}
                    </dl>
                  </Field>
                )}
              </Section>
            </div>
          )}
        </div>

        {detail && (
          <div className="px-4 py-3 bg-background border-t flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate(`/admin-dashboard/events/${eventId}/messages?team=${detail.team_id}`)}
            >
              <Send className="w-3 h-3" /> Send Message
            </Button>
            <Button size="sm" variant="outline" onClick={handleToggleLock}>
              {detail.roster_locked ? <Unlock className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
              {detail.roster_locked ? "Unlock Roster" : "Lock Roster"}
            </Button>
            <Button size="sm" variant="outline" onClick={handleToggleCheckIn}>
              {detail.checked_in_at ? <XCircle className="w-3 h-3" /> : <CheckCircle2 className="w-3 h-3" />}
              {detail.checked_in_at ? "Undo Check-In" : "Mark Checked In"}
            </Button>
            {detail.payment_status === "paid" && detail.stripe_payment_intent_id && (
              <Button size="sm" variant="outline" asChild>
                <a
                  href={`https://dashboard.stripe.com/payments/${detail.stripe_payment_intent_id}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <ExternalLink className="w-3 h-3" /> Refund (Stripe)
                </a>
              </Button>
            )}
            {(detail.payment_status === "unpaid" || detail.payment_status === "partial") && (
              <Button
                size="sm"
                variant="outline"
                onClick={async () => {
                  try {
                    const { error } = await invokeEdgeFunction("send-payment-reminders", {
                      body: { registration_id: detail.id },
                    });
                    if (error) throw error;
                    toast.success("Payment reminder sent");
                  } catch (e: any) {
                    toast.error(`Reminder failed: ${e?.message ?? e}`);
                  }
                }}
              >
                <Send className="w-3 h-3" /> Send Payment Reminder
              </Button>
            )}
            <Button
              size="sm"
              variant="destructive"
              onClick={() => {
                setDeleteConfirmText("");
                setConfirmDelete(true);
              }}
            >
              <Trash2 className="w-3 h-3" /> Delete
            </Button>
          </div>
        )}
      </div>

      {/* Mark payment dialog */}
      <Dialog open={confirmPayOpen} onOpenChange={setConfirmPayOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark payment received</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm">Amount</label>
              <Input
                type="number"
                value={payAmount}
                onChange={(e) => setPayAmount(e.target.value)}
                placeholder={String(detail?.total_amount_due || "")}
              />
            </div>
            <div>
              <label className="text-sm">Method</label>
              <Input value={payMethod} onChange={(e) => setPayMethod(e.target.value)} placeholder="check, cash, wire…" />
            </div>
            <div>
              <label className="text-sm">Reference</label>
              <Input value={payReference} onChange={(e) => setPayReference(e.target.value)} placeholder="check #, txn id…" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmPayOpen(false)}>Cancel</Button>
            <Button onClick={handleMarkPayment}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email change warning */}
      <Dialog open={!!emailWarnPending} onOpenChange={(v) => !v && setEmailWarnPending(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change account email?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            This will also update the account login email. The coach will use this email to sign in.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailWarnPending(null)}>Cancel</Button>
            <Button
              onClick={async () => {
                try {
                  await handleEmailChange(emailWarnPending!);
                  setEmailWarnPending(null);
                } catch (e: any) {
                  toast.error(e?.message || "Failed");
                }
              }}
            >
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete registration?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Type <strong>{teamName}</strong> to confirm. This cannot be undone.
          </p>
          <Input value={deleteConfirmText} onChange={(e) => setDeleteConfirmText(e.target.value)} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(false)}>Cancel</Button>
            <Button
              variant="destructive"
              disabled={deleteConfirmText !== teamName}
              onClick={handleDelete}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
